import { zodResolver } from "@hookform/resolvers/zod";
import { Add } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";

import { useCreateContractMutation } from "AdvisoryManagement/Api/Mutations/useCreateContractMutation";
import { usePartnersQuery } from "AdvisoryManagement/Api/Queries/usePartnersQuery";
import { useProductCategoriesQuery } from "AdvisoryManagement/Api/Queries/useProductCategoriesQuery";
import { useProductsQuery } from "AdvisoryManagement/Api/Queries/useProductsQuery";
import { getContractTypeOptions } from "AdvisoryManagement/Components/Forms/utils";
import { useAdvisoryManagementResource } from "AdvisoryManagement/Translations/AdvisoryManagementResource";
import { BlFormAutocomplete } from "Forms/Components/BlFormAutocomplete";
import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlHiddenInput } from "Forms/Components/BlHiddenInput";
import { ContractStatus, ContractType } from "Infrastructure/Api/Api";
import { SidebarPanel } from "Shared/Sidebar";

export const AddContract = () => {
	const [sidebarOpen, setSidebarOpen] = useState(false);

	const createContract = useCreateContractMutation();

	const handleCreateContract = async (data: AddContractFormValues) => {
		try {
			await createContract.mutateAsync(data);

			toast.success("Smlouva byla vytvořena.");
		} catch (error) {
			toast.error("Nastala chyba při vytváření smlouvy.");
			console.error(error);
		} finally {
			handleClose();
		}
	};

	const handleClose = () => {
		setSidebarOpen(false);
	};

	const handleOpen = () => {
		setSidebarOpen(true);
	};

	return (
		<>
			<Button
				variant="text"
				color="primary"
				sx={{ marginLeft: "auto" }}
				endIcon={<Add />}
				onClick={handleOpen}
				component="span"
			>
				Přidat smlouvu
			</Button>
			{sidebarOpen && (
				<AddContractSidebar
					onClose={handleClose}
					onSubmit={handleCreateContract}
				/>
			)}
		</>
	);
};

type AddContractSidebarProps = {
	onClose: () => void;
	onSubmit: (data: AddContractFormValues) => void | Promise<void>;
	initialValues?: Partial<AddContractFormValues>;
	presetMainCategoryCode?: string;
};

const createContractSchema = z.object({
	advisoryCaseId: z.string(),
	categoryCode: z.string(),
	partnerCode: z.string(),
	productCode: z.string(),
	contractType: z.enum([ContractType.Modification, ContractType.New]),
	status: z.enum([ContractStatus.InProgress]),
});

type AddContractFormValues = z.infer<typeof createContractSchema>;

export const AddContractSidebar = ({
	onClose,
	onSubmit,
	initialValues,
	presetMainCategoryCode,
}: AddContractSidebarProps) => {
	const { advisoryCaseId } = useParams<{ advisoryCaseId: string }>();
	const { t } = useAdvisoryManagementResource();

	const form = useForm<AddContractFormValues>({
		resolver: zodResolver(createContractSchema),
		defaultValues: initialValues || {
			advisoryCaseId: advisoryCaseId ?? "",
			categoryCode: "",
			partnerCode: "",
			productCode: "",
			contractType: ContractType.New,
			status: ContractStatus.InProgress,
		},
	});

	const categoryCode = form.watch("categoryCode");
	const partnerCode = form.watch("partnerCode");

	const productCategories = useProductCategoriesQuery();
	const partners = usePartnersQuery(categoryCode, {
		enabled: !!categoryCode,
	});
	const products = useProductsQuery(categoryCode, partnerCode, {
		enabled: !!categoryCode && !!partnerCode,
	});

	const groupedCategories = useMemo(() => {
		if (!productCategories.data) return [];

		const categories = productCategories.data;

		const masterCategories = presetMainCategoryCode
			? categories.filter((c) => c.code === presetMainCategoryCode)
			: categories.filter((c) => c.level === 0);

		return masterCategories
			.map((master) => {
				// Get all items (level 1 and 2) that belong to this master category
				const items = categories.filter((item) => {
					if (item.level === 0) return false;

					// Extract parent category code from path
					// For level 1: "CAT_MASTER_ALL.CAT_INVESTMENTS" -> parent is CAT_INVESTMENTS
					// For level 2: "CAT_MASTER_ALL.CAT_LIFE.CAT_L_INSURANCE" -> parent is CAT_LIFE
					const pathParts = item.path.split(".");
					const parentCode = pathParts[1]; // Second part is always the master category

					return parentCode === master.code;
				});

				return {
					group: master.name,
					items: items.map((item) => ({
						code: item.code,
						name: item.name,
					})),
				};
			})
			.filter((group) => group.items.length > 0);
	}, [productCategories.data, presetMainCategoryCode]);

	const handleClose = () => {
		form.reset(
			initialValues || {
				advisoryCaseId: advisoryCaseId ?? "",
				categoryCode: "",
				partnerCode: "",
				productCode: "",
				status: ContractStatus.InProgress,
			},
		);
		onClose?.();
	};

	return (
		<SidebarPanel open={true} onClose={handleClose} title="Vytvořit smlouvu">
			<Stack spacing={3}>
				<Typography variant="body2" color="text.secondary">
					Vyberte kategorii produktu smlouvy, kterou chcete vytvořit.
				</Typography>
				<form onSubmit={form.handleSubmit(onSubmit)}>
					<Stack direction="column" spacing={2}>
						<BlHiddenInput name="advisoryCaseId" control={form.control} />
						<BlFormAutocomplete
							control={form.control}
							name="categoryCode"
							label="Kategorie produktu"
							placeholder="Vyberte kategorii produktu"
							groupedCodeList={groupedCategories}
							errors={form.formState.errors}
							loading={productCategories.isLoading}
							onChange={() => {
								form.setValue("partnerCode", "");
								form.setValue("productCode", "");
							}}
						/>
						<BlFormAutocomplete
							control={form.control}
							name="partnerCode"
							label="Instituce"
							placeholder="Vyberte instituci"
							codeList={
								partners.data?.map((p) => ({
									code: p.code,
									name: p.name,
								})) ?? []
							}
							errors={form.formState.errors}
							disabled={!categoryCode}
							loading={partners.isLoading}
							onChange={() => {
								form.setValue("productCode", "");
							}}
						/>
						<BlFormAutocomplete
							control={form.control}
							name="productCode"
							label="Produkt"
							placeholder="Vyberte produkt"
							codeList={
								products.data?.map((p) => ({
									code: p.code,
									name: p.name,
								})) ?? []
							}
							disabled={!partnerCode}
							loading={products.isLoading}
							errors={form.formState.errors}
						/>
						<BlFormDropdown
							control={form.control}
							name="contractType"
							label="Stav smlouvy"
							placeholder="Vyberte stav smlouvy"
							codeList={getContractTypeOptions(t as TFunction)}
							errors={form.formState.errors}
						/>
					</Stack>
				</form>
				<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
					<Button onClick={handleClose}>Zrušit</Button>
					<Button onClick={form.handleSubmit(onSubmit)} variant="contained">
						Přidat
					</Button>
				</Box>
			</Stack>
		</SidebarPanel>
	);
};

// Legacy alias for backwards compatibility
export const AddContractDialog = AddContractSidebar;
